import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import AutoComplete from "../../../../Components/UI/Inputs/AutoComplete";
import BoltButton from "../../../../Components/UI/Buttons/BoltButton";
import SwitchButton from "../../../../Components/SwitchButton/SwitchButton";
import {
  fetchCustomCarriers,
  allowAdminAccount,
  updateAllowedUserAccount,
  fetchAllowedAccounts,
} from "../../../../services/AdminServices/Companies/companies.services";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

const AllowedCarriers = ({ shipperData }) => {
  const { t } = useTranslation("admin");
  const dispatch = useDispatch();
  const [accountsOptions, setAccountsOptions] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [loading, setLoading] = useState(true);
  const [allowing, setAllowing] = useState(false);
  const [allowedAccounts, setAllowedAccounts] = useState([]);
  const [allowedLoading, setAllowedLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [editingMarkupId, setEditingMarkupId] = useState(null);
  const [markupValue, setMarkupValue] = useState("");
  const [updatingMarkup, setUpdatingMarkup] = useState(false);
  const [allowedMeta, setAllowedMeta] = useState({
    page: 1,
    perPage: 20,
    lastPage: 1,
    total: 0,
  });

  const shipperId = shipperData?.id;

  useEffect(() => {
    const loadAccounts = async () => {
      setLoading(true);
      const res = await fetchCustomCarriers(1, 1000, "");
      if (!res.error) {
        const list = res?.data?.result?.data || [];
        const mapped = list.map((acc) => ({
          id: acc?.id,
          title: `${acc?.shipping_company?.name || acc?.name} - ${
            acc?.account_name || acc?.account_no || i18n.t("admin:controls.accountFallback")
          }`,
          raw: acc,
        }));
        setAccountsOptions(mapped);
      } else {
        const message =
          res?.error?.response?.data?.message ||
          res?.error?.message ||
          i18n.t("admin:controls.failedToLoadAccounts");
        dispatch(
          showSnackBar({
            text: message,
            severity: "error",
          })
        );
      }
      setLoading(false);
    };
    const loadAllowed = async (page, perPage) => {
      if (!shipperId) return;
      setAllowedLoading(true);
      const res = await fetchAllowedAccounts(shipperId, page, perPage);
      if (!res.error) {
        const payload = res?.data?.result || res?.data || {};
        const list = payload?.data || [];
        setAllowedAccounts(
          list.map((item) => {
            const detailsFromAllowed =
              item?.details || item?.user_shipping_company;
            return {
              ...item,
              is_enabled: item?.is_enabled,
              details: detailsFromAllowed || undefined,
            };
          })
        );
        setAllowedMeta({
          page: Number(payload?.current_page || page || 1),
          perPage: Number(payload?.per_page || perPage || 20),
          lastPage: Number(payload?.last_page || 1),
          total: Number(payload?.total || 0),
        });
      } else {
        const message =
          res?.error?.response?.data?.message ||
          res?.error?.message ||
          i18n.t("admin:controls.failedToLoadAllowedAccounts");
        dispatch(
          showSnackBar({
            text: message,
            severity: "error",
          })
        );
      }
      setAllowedLoading(false);
    };
    (async () => {
      await loadAccounts();
      await loadAllowed(1, 20);
    })();
  }, [dispatch, shipperId]);

  useEffect(() => {
    if (!accountsOptions?.length || !allowedAccounts?.length) return;
    setAllowedAccounts((prev) =>
      prev.map((item) => {
        if (item?.details) return item;
        const detailsFromOptions = accountsOptions.find(
          (o) => `${o?.raw?.id}` === `${item?.users_shipping_company_id}`
        )?.raw;
        return detailsFromOptions
          ? { ...item, details: detailsFromOptions }
          : item;
      })
    );
  }, [accountsOptions, allowedAccounts?.length]);

  const selectedAccount = useMemo(
    () =>
      accountsOptions.find((o) => `${o?.raw?.id}` === `${selectedAccountId}`),
    [accountsOptions, selectedAccountId]
  );

  const handleAllow = async () => {
    if (!selectedAccount?.raw?.id || !shipperId) return;
    setAllowing(true);
    const payload = {
      user_id: shipperId,
      users_shipping_company_id: selectedAccount.raw.id,
    };
    const res = await allowAdminAccount(payload);
    setAllowing(false);
    if (res.error) {
      const message =
        res?.error?.response?.data?.message ||
        res?.error?.message ||
        i18n.t("admin:controls.failedToAllowAccount");
      dispatch(
        showSnackBar({
          text: message,
          severity: "error",
        })
      );
      return;
    }
    await (async () => {
      const fetchRes = await fetchAllowedAccounts(
        shipperId,
        allowedMeta.page,
        allowedMeta.perPage
      );
      if (!fetchRes.error) {
        const payload = fetchRes?.data?.result || fetchRes?.data || {};
        const list = payload?.data || [];
        setAllowedAccounts(
          list.map((item) => {
            const detailsFromOptions = accountsOptions.find(
              (o) => `${o?.raw?.id}` === `${item?.users_shipping_company_id}`
            )?.raw;
            return {
              ...item,
              details: item?.user_shipping_company || detailsFromOptions,
            };
          })
        );
        setAllowedMeta({
          page: Number(payload?.current_page || allowedMeta.page || 1),
          perPage: Number(payload?.per_page || allowedMeta.perPage || 20),
          lastPage: Number(payload?.last_page || 1),
          total: Number(payload?.total || 0),
        });
      }
    })();
    setSelectedAccountId("");
    dispatch(
      showSnackBar({
        text: t("controls.accountAllowed"),
        severity: "success",
      })
    );
  };

  const toggleAllowed = async (record, check) => {
    const id = record?.id;
    setUpdatingId(id);
    const isEnabledValue = check ? "1" : "0";
    setAllowedAccounts((prev) =>
      prev.map((r) => (r.id === id ? { ...r, is_enabled: isEnabledValue } : r))
    );
    const res = await updateAllowedUserAccount(
      { is_enabled: isEnabledValue },
      id
    );
    if (res.error) {
      const message =
        res?.error?.response?.data?.message ||
        res?.error?.message ||
        i18n.t("admin:controls.failedToUpdateStatus");
      dispatch(
        showSnackBar({
          text: message,
          severity: "error",
        })
      );
      setAllowedAccounts((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, is_enabled: `${record?.is_enabled}` } : r
        )
      );
      setUpdatingId(null);
      return;
    }
    dispatch(
      showSnackBar({
        text: t("controls.statusUpdatedSuccessfully"),
        severity: "success",
      })
    );
    setUpdatingId(null);
  };

  const handleMarkupEdit = (record) => {
    setEditingMarkupId(record.id);
    setMarkupValue(record.markup || "");
  };

  const handleMarkupSave = async (record) => {
    if (editingMarkupId !== record.id) return;

    setUpdatingMarkup(true);
    const res = await updateAllowedUserAccount(
      { markup: markupValue },
      record.id
    );
    setUpdatingMarkup(false);

    if (res.error) {
      const message =
        res?.error?.response?.data?.message ||
        res?.error?.message ||
        i18n.t("admin:controls.failedToUpdateMarkup");
      dispatch(
        showSnackBar({
          text: message,
          severity: "error",
        })
      );
    } else {
      setAllowedAccounts((prev) =>
        prev.map((r) =>
          r.id === record.id ? { ...r, markup: markupValue } : r
        )
      );
      dispatch(
        showSnackBar({
          text: t("controls.markupUpdatedSuccessfully"),
          severity: "success",
        })
      );
    }
    setEditingMarkupId(null);
    setMarkupValue("");
  };

  const handleMarkupCancel = () => {
    setEditingMarkupId(null);
    setMarkupValue("");
  };

  const renderedAllowed = useMemo(() => allowedAccounts, [allowedAccounts]);

  const loadAllowedPage = async (targetPage) => {
    setAllowedLoading(true);
    const res = await fetchAllowedAccounts(
      shipperId,
      targetPage,
      allowedMeta.perPage
    );
    if (!res.error) {
      const payload = res?.data?.result || res?.data || {};
      const list = payload?.data || [];
      setAllowedAccounts(
        list.map((item) => {
          const detailsFromOptions = accountsOptions.find(
            (o) => `${o?.raw?.id}` === `${item?.users_shipping_company_id}`
          )?.raw;
          return {
            ...item,
            details: item?.user_shipping_company || detailsFromOptions,
          };
        })
      );
      setAllowedMeta({
        page: Number(payload?.current_page || targetPage),
        perPage: Number(payload?.per_page || allowedMeta.perPage),
        lastPage: Number(payload?.last_page || 1),
        total: Number(payload?.total || 0),
      });
    }
    setAllowedLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Header + Actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-3 bg-brand-light rounded-lg border border-brand">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-brand-light rounded-lg">
            <svg
              className="w-6 h-6 text-brand"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ui-text truncate">
              {t("controls.allowedCarriers")}
            </h2>
            <p className="text-xs text-ui-text-muted truncate">
              {t("controls.manageShippersAllowedCarriersAnd")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="w-full md:w-80">
            <AutoComplete
              options={accountsOptions.map((o) => ({
                label: o.title,
                value: `${o.raw.id}`,
              }))}
              selected={`${selectedAccountId}`}
              setSelected={setSelectedAccountId}
              placeholder={
                loading ? t("controls.loadingAccounts") : t("controls.searchAdminCustomAccounts")
              }
            />
          </div>
          <BoltButton
            label={t("controls.allow")}
            onClick={handleAllow}
            disabled={!selectedAccount || !shipperId}
            loading={allowing}
            variant="primary"
            size="md"
          />
        </div>
      </div>

      <div className="bg-ui-surface rounded-lg border border-ui-border p-3">
        <h3 className="text-sm font-semibold text-ui-text mb-2">
          {t("controls.allowedAccounts")}
        </h3>

        {allowedLoading ? (
          <div className="space-y-1.5">
            {Array.from({ length: 5 }).map((_, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between py-1.5 px-2 bg-ui-surface-sunken rounded-md animate-pulse"
              >
                <div className="flex items-center gap-3 min-w-0 w-full">
                  <div className="w-6 h-6 bg-ui-border rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="h-3 bg-ui-border rounded w-40 mb-1" />
                    <div className="h-2.5 bg-ui-border rounded w-28" />
                  </div>
                </div>
                <div className="w-10 h-6 bg-ui-border rounded-full" />
              </div>
            ))}
          </div>
        ) : renderedAllowed?.length ? (
          <div className="space-y-1.5">
            {renderedAllowed.map((acc) => (
              <div
                key={acc?.id || acc?.users_shipping_company_id}
                className="flex items-center justify-between py-2 px-3 bg-ui-surface-sunken hover:bg-ui-surface-sunken rounded-md"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {acc?.details?.shipping_company?.logo_url && (
                    <img
                      src={acc.details.shipping_company.logo_url}
                      alt={t("controls.logo")}
                      className="w-6 h-6 rounded object-contain"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ui-text truncate">
                      {acc?.details?.shipping_company?.name ||
                        acc?.details?.name}
                    </div>
                    <div className="text-xs text-ui-text-muted truncate">
                      {acc?.details?.account_name || acc?.details?.account_no}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-ui-text-muted">
                      {t("controls.markupLabel")}
                    </span>
                    {editingMarkupId === acc?.id ? (
                      <div className="flex items-center gap-2 bg-ui-surface border border-brand rounded-lg px-2 py-1 shadow-sm">
                        <input
                          type="number"
                          value={markupValue}
                          onChange={(e) => setMarkupValue(e.target.value)}
                          className="w-20 h-7 px-2 text-sm border-0 focus:outline-none focus:ring-0 bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          autoFocus
                        />
                        <span className="text-xs text-ui-text-muted">%</span>
                        <div className="flex items-center gap-1 ml-1">
                          <button
                            onClick={() => handleMarkupSave(acc)}
                            disabled={updatingMarkup}
                            className="p-1.5 text-state-success hover:text-state-success hover:bg-state-success-bg rounded disabled:opacity-50 transition-colors"
                            title={t("controls.saveMarkup")}
                          >
                            {updatingMarkup ? (
                              <svg
                                className="w-4 h-4 animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={handleMarkupCancel}
                            className="p-1.5 text-state-danger hover:text-state-danger hover:bg-state-danger-bg rounded transition-colors"
                            title={t("controls.cancelEditing")}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="flex items-center gap-2 bg-ui-surface-sunken hover:bg-ui-border rounded-lg px-3 py-1.5 cursor-pointer transition-colors group"
                        onClick={() => handleMarkupEdit(acc)}
                      >
                        <span className="text-sm font-medium text-ui-text">
                          {acc?.markup > 0 ? acc?.markup : t("controls.default")} %
                        </span>
                        <svg
                          className="w-3.5 h-3.5 text-ui-text-subtle group-hover:text-ui-text-muted transition-colors"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        <span className="text-xs text-ui-text-muted group-hover:text-ui-text-muted transition-colors">
                          {t("controls.clickToEdit")}
                        </span>
                      </div>
                    )}
                  </div>

                  <div
                    className={`${
                      updatingId === acc?.id
                        ? "opacity-60 pointer-events-none"
                        : ""
                    }`}
                  >
                    <SwitchButton
                      checked={
                        `${acc?.is_enabled}` === "1" || acc?.is_enabled === true
                      }
                      onChange={(e) => toggleAllowed(acc, e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-ui-text-muted">{t("controls.noAccountsAllowedYet")}</div>
        )}

        {allowedMeta.lastPage > 1 && (
          <div className="flex items-center justify-between pt-2 mt-2 border-t border-ui-border text-xs text-ui-text-muted">
            <span>
              {t("controls.pageOf", {
                page: allowedMeta.page,
                last: allowedMeta.lastPage,
                total: allowedMeta.total,
              })}
            </span>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                disabled={allowedMeta.page <= 1}
                onClick={() =>
                  loadAllowedPage(Math.max(1, allowedMeta.page - 1))
                }
                className={`px-2 py-1 rounded border ${
                  allowedMeta.page <= 1
                    ? "text-ui-border border-ui-border cursor-not-allowed"
                    : "text-ui-text hover:bg-ui-surface-sunken"
                }`}
              >
                {t("controls.prev")}
              </button>
              {Array.from(
                { length: allowedMeta.lastPage },
                (_, i) => i + 1
              ).map((p) => (
                <button
                  key={p}
                  onClick={() => loadAllowedPage(p)}
                  className={`px-2 py-1 rounded border ${
                    p === allowedMeta.page
                      ? "bg-brand text-white border-brand"
                      : "text-ui-text hover:bg-ui-surface-sunken"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                disabled={allowedMeta.page >= allowedMeta.lastPage}
                onClick={() =>
                  loadAllowedPage(
                    Math.min(allowedMeta.lastPage, allowedMeta.page + 1)
                  )
                }
                className={`px-2 py-1 rounded border ${
                  allowedMeta.page >= allowedMeta.lastPage
                    ? "text-ui-border border-ui-border cursor-not-allowed"
                    : "text-ui-text hover:bg-ui-surface-sunken"
                }`}
              >
                {t("common:actions.next")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllowedCarriers;
